import { useEffect, useRef, useState } from "react";

const testimonials = [
  {
    name: "Margaret T.",
    age: "62",
    location: "Florida, USA",
    image:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20happy%20senior%20woman%20in%20her%2060s%20with%20warm%20genuine%20smile%2C%20outdoor%20natural%20light%2C%20soft%20golden%20hour%20lighting%2C%20casual%20elegant%20attire%2C%20approachable%20friendly%20expression%2C%20editorial%20portrait%20photography%2C%20warm%20neutral%20background%2C%20soft%20focus&width=200&height=200&seq=testimonial-margaret-2026&orientation=squarish",
    text: "After two months, I noticed I was walking my dog again without stopping halfway. My knees feel supported in a way I had not experienced in years. I am grateful I gave this a real chance.",
    stars: 5,
  },
  {
    name: "Robert K.",
    age: "58",
    location: "Arizona, USA",
    image:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20confident%20senior%20man%20in%20his%20late%2050s%20with%20warm%20friendly%20smile%2C%20outdoor%20natural%20light%2C%20soft%20warm%20lighting%2C%20casual%20button%20down%20shirt%2C%20approachable%20expression%2C%20editorial%20portrait%20photography%2C%20neutral%20warm%20background%2C%20soft%20focus&width=200&height=200&seq=testimonial-robert-2026&orientation=squarish",
    text: "I was skeptical at first. But after 90 days, my morning stiffness became noticeably shorter. I am back to golfing twice a week and actually enjoying it again. This changed my routine completely.",
    stars: 5,
  },
  {
    name: "Helena M.",
    age: "67",
    location: "California, USA",
    image:
      "https://readdy.ai/api/search-image?query=Portrait%20of%20an%20elegant%20senior%20woman%20in%20her%20late%2060s%20with%20serene%20warm%20smile%2C%20natural%20outdoor%20light%2C%20soft%20golden%20lighting%2C%20casual%20sophisticated%20attire%2C%20peaceful%20content%20expression%2C%20editorial%20portrait%20photography%2C%20warm%20neutral%20background%2C%20soft%20focus&width=200&height=200&seq=testimonial-helena-2026&orientation=squarish",
    text: "Gardening was becoming painful, and I almost gave it up. Within weeks of starting Joint Genesis, I felt a real difference in how my hands and knees moved. I am planting my roses again without hesitation.",
    stars: 5,
  },
];

const stats = [
  { value: "47,000+", label: "Satisfied Customers" },
  { value: "4.9/5", label: "Average Rating" },
  { value: "180", label: "Days Guarantee" },
];

export default function SocialProofSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)" }}
        >
          <p className="text-accent-500 text-base font-medium tracking-[0.2em] uppercase mb-3">
            Real Results From Real People
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            Trusted by Over 47,000 Adults
            <br />
            Who Refused to Slow Down
          </h2>
          <p className="text-foreground-700 text-xl max-w-2xl mx-auto">
            Thousands of men and women over 50 have already made Joint Genesis
            part of their daily routine — and the feedback speaks for itself.
          </p>
        </div>

        {/* Stats */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-3 gap-6 mb-14 md:mb-20 transition-all duration-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          style={{
            transitionDelay: "80ms",
            transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
          }}
        >
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70"
            >
              <p className="font-heading text-4xl md:text-5xl font-bold text-accent-600 mb-2">
                {stat.value}
              </p>
              <p className="text-foreground-600 text-base font-medium tracking-wide">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-14">
          {testimonials.map((t, idx) => (
            <div
              key={t.name}
              className={`rounded-xl border border-background-200/70 bg-background-100 p-6 md:p-8 card-hover-lift transition-all duration-600 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
              }`}
              style={{
                transitionDelay: `${160 + idx * 80}ms`,
                transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
              }}
            >
              {/* Stars */}
              <div className="flex items-center gap-1 mb-5">
                {Array.from({ length: t.stars }).map((_, i) => (
                  <i
                    key={i}
                    className="ri-star-fill text-amber-400 text-lg"
                  />
                ))}
                <span className="text-foreground-500 text-sm ml-2 font-medium">
                  Verified Purchase
                </span>
              </div>

              {/* Quote */}
              <blockquote className="text-foreground-700 text-base leading-relaxed mb-6">
                &ldquo;{t.text}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={t.image}
                    alt={`Portrait of ${t.name}`}
                    className="w-full h-full object-cover"
                    width={200}
                    height={200}
                  />
                </div>
                <div>
                  <p className="font-heading text-lg font-semibold text-foreground-900">
                    {t.name}
                  </p>
                  <p className="text-foreground-500 text-sm">
                    Age {t.age}, {t.location}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div
          className={`flex flex-wrap items-center justify-center gap-6 md:gap-10 transition-all duration-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          style={{
            transitionDelay: "400ms",
            transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
          }}
        >
          <div className="flex items-center gap-2 text-foreground-500">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-shield-check-line text-primary-600 text-lg" />
            </div>
            <span className="text-sm font-medium">180-Day Guarantee</span>
          </div>
          <div className="flex items-center gap-2 text-foreground-500">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-leaf-line text-accent-600 text-lg" />
            </div>
            <span className="text-sm font-medium">Natural Ingredients</span>
          </div>
          <div className="flex items-center gap-2 text-foreground-500">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-truck-line text-secondary-600 text-lg" />
            </div>
            <span className="text-sm font-medium">Fast Shipping</span>
          </div>
          <div className="flex items-center gap-2 text-foreground-500">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-secure-payment-line text-primary-600 text-lg" />
            </div>
            <span className="text-sm font-medium">Secure Checkout</span>
          </div>
        </div>
      </div>
    </section>
  );
}